import React, { useCallback, useMemo, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DashboardFilters from '../../components/dashboard/DashboardFilters';
import KPICards from '../../components/dashboard/KPICards';
import SalesTrend from '../../components/dashboard/SalesTrend';
import SalesByChannel from '../../components/dashboard/SalesByChannel';
import SchemeDistribution from '../../components/dashboard/SchemeDistribution';
import SettlementSummary from '../../components/dashboard/SettlementSummary';
import BranchPerformance from '../../components/dashboard/BranchPerformance';
import TerminalPerformance from '../../components/dashboard/TerminalPerformance';
import RecentTransactions from '../../components/dashboard/RecentTransactions';
import AlertsPanel from '../../components/dashboard/AlertsPanel';
import { useAuth } from '../../context/AuthContext';
import { getKPIData, getTrendData, getChannelData, getSchemeData, getSettlementData, getBranchData, getTerminalData, getRecentTransactions, getAlerts } from '../../utils/dashboardData';
// Time-based multipliers to simulate different data for different periods
const TIME_MULTIPLIERS: Record<string, number> = {
  Today: 1.0,
  Day: 0.85,
  Week: 5.2,
  MTD: 22.5,
  YTD: 265.0
};
// Channel-based weight factors (what fraction of total each channel represents)
const CHANNEL_WEIGHTS: Record<string, number> = {
  POS: 0.32,
  SoftPOS: 0.15,
  'Mobile POS': 0.12,
  'E-Commerce': 0.28,
  'RAAST QR': 0.06,
  'RAAST RTP': 0.07
};
// Scheme-based weight factors
const SCHEME_WEIGHTS: Record<string, number> = {
  Visa: 0.44,
  MasterCard: 0.34,
  UnionPay: 0.1,
  PayPak: 0.07,
  RAAST: 0.05
};
const NewDashboardPage = () => {
  const {
    user
  } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    time: 'Today',
    channel: 'All Channels',
    scheme: 'All Schemes',
    terminal: 'All Terminals',
    branch: 'All Branches'
  });
  // Load base mock data
  const kpiData = useMemo(() => getKPIData(), []);
  const trendData = useMemo(() => getTrendData(), []);
  const channelData = useMemo(() => getChannelData(), []);
  const schemeData = useMemo(() => getSchemeData(), []);
  const settlementData = useMemo(() => getSettlementData(), []);
  const branchData = useMemo(() => getBranchData(), []);
  const terminalData = useMemo(() => getTerminalData(), []);
  const recentTransactions = useMemo(() => getRecentTransactions(), []);
  const alerts = useMemo(() => getAlerts(), []);
  // Compute a combined filter multiplier based on active filters
  const filterMultiplier = useMemo(() => {
    let multiplier = TIME_MULTIPLIERS[filters.time] || 1.0;
    // If a specific channel is selected, scale down to that channel's share
    if (filters.channel !== 'All Channels') {
      multiplier *= CHANNEL_WEIGHTS[filters.channel] || 0.15;
    }
    // If a specific scheme is selected, scale down to that scheme's share
    if (filters.scheme !== 'All Schemes') {
      multiplier *= SCHEME_WEIGHTS[filters.scheme] || 0.1;
    }
    return multiplier;
  }, [filters.time, filters.channel, filters.scheme]);
  // Filtered KPI data - apply multiplier to values
  const filteredKPIData = useMemo(() => {
    return kpiData.map(kpi => {
      if (kpi.type === 'percentage') return kpi; // Don't scale percentages
      const numericValue = parseFloat(kpi.value.replace(/,/g, ''));
      const scaledValue = Math.round(numericValue * filterMultiplier);
      const formattedValue = scaledValue.toLocaleString();
      // Vary the trend slightly based on time filter to make it feel dynamic
      const trendVariation = filters.time === 'Today' ? 0 : filters.time === 'Week' ? 1.5 : filters.time === 'MTD' ? -0.8 : filters.time === 'YTD' ? 2.3 : -0.5;
      return {
        ...kpi,
        value: formattedValue,
        trend: parseFloat((kpi.trend + trendVariation).toFixed(1))
      };
    });
  }, [kpiData, filterMultiplier, filters.time]);
  // Filtered trend data - apply time multiplier
  const filteredTrendData = useMemo(() => {
    const timeMult = TIME_MULTIPLIERS[filters.time] || 1.0;
    const channelMult = filters.channel !== 'All Channels' ? CHANNEL_WEIGHTS[filters.channel] || 0.15 : 1.0;
    const schemeMult = filters.scheme !== 'All Schemes' ? SCHEME_WEIGHTS[filters.scheme] || 0.1 : 1.0;
    const mult = timeMult * channelMult * schemeMult;
    return trendData.map(d => ({
      ...d,
      currentValue: Math.round(d.currentValue * mult),
      previousValue: Math.round(d.previousValue * mult),
      currentCount: Math.round(d.currentCount * mult),
      previousCount: Math.round(d.previousCount * mult)
    }));
  }, [trendData, filters.time, filters.channel, filters.scheme]);
  // Filtered channel data - if a specific channel is selected, zero out others
  const filteredChannelData = useMemo(() => {
    const timeMult = TIME_MULTIPLIERS[filters.time] || 1.0;
    return channelData.map(d => {
      const base = {
        ...d
      };
      // Apply time multiplier
      base.pos = Math.round(d.pos * timeMult);
      base.softPos = Math.round(d.softPos * timeMult);
      base.mobilePos = Math.round(d.mobilePos * timeMult);
      base.eCommerce = Math.round(d.eCommerce * timeMult);
      base.raastQr = Math.round(d.raastQr * timeMult);
      base.raastRtp = Math.round(d.raastRtp * timeMult);
      // If a specific channel is selected, zero out others
      if (filters.channel !== 'All Channels') {
        const ch = filters.channel;
        if (ch !== 'POS') base.pos = 0;
        if (ch !== 'SoftPOS') base.softPos = 0;
        if (ch !== 'Mobile POS') base.mobilePos = 0;
        if (ch !== 'E-Commerce') base.eCommerce = 0;
        if (ch !== 'RAAST QR') base.raastQr = 0;
        if (ch !== 'RAAST RTP') base.raastRtp = 0;
      }
      return base;
    });
  }, [channelData, filters.time, filters.channel]);
  // Filtered scheme data
  const filteredSchemeData = useMemo(() => {
    let filtered = filters.scheme === 'All Schemes' ? schemeData : schemeData.filter(s => s.name === filters.scheme);
    // Apply time multiplier to values
    const timeMult = TIME_MULTIPLIERS[filters.time] || 1.0;
    filtered = filtered.map(s => ({
      ...s,
      value: Math.round(s.value * timeMult),
      count: Math.round(s.count * timeMult)
    }));
    // Recalculate percentages
    const totalValue = filtered.reduce((sum, s) => sum + s.value, 0);
    if (totalValue > 0) {
      filtered = filtered.map(s => ({
        ...s,
        percentage: parseFloat((s.value / totalValue * 100).toFixed(1))
      }));
    }
    return filtered;
  }, [schemeData, filters.scheme, filters.time]);
  // Filtered settlement data
  const filteredSettlementData = useMemo(() => {
    const timeMult = TIME_MULTIPLIERS[filters.time] || 1.0;
    return {
      ...settlementData,
      today: Math.round(settlementData.today * timeMult),
      upcoming: Math.round(settlementData.upcoming * timeMult),
      monthTotal: Math.round(settlementData.monthTotal * (filters.time === 'YTD' ? 12 : 1))
    };
  }, [settlementData, filters.time]);
  // Filtered branch data
  const filteredBranchData = useMemo(() => {
    let filtered = filters.branch === 'All Branches' ? branchData : branchData.filter(b => b.name === filters.branch);
    const timeMult = TIME_MULTIPLIERS[filters.time] || 1.0;
    return filtered.map(b => ({
      ...b,
      transactions: Math.round(b.transactions * timeMult),
      salesValue: Math.round(b.salesValue * timeMult)
    }));
  }, [branchData, filters.branch, filters.time]);
  // Filtered terminal data
  const filteredTerminalData = useMemo(() => {
    let filtered = terminalData;
    // Filter by channel → terminal type mapping
    if (filters.channel !== 'All Channels') {
      const channelToType: Record<string, string> = {
        POS: 'POS',
        SoftPOS: 'SoftPOS',
        'Mobile POS': 'MobilePOS'
      };
      const terminalType = channelToType[filters.channel];
      if (terminalType) {
        filtered = filtered.filter(t => t.type === terminalType);
      }
      // For E-Commerce, RAAST QR, RAAST RTP — no terminal type match, show all
    }
    const timeMult = TIME_MULTIPLIERS[filters.time] || 1.0;
    return filtered.map(t => ({
      ...t,
      transactions: Math.round(t.transactions * timeMult),
      salesValue: Math.round(t.salesValue * timeMult)
    }));
  }, [terminalData, filters.channel, filters.time]);
  // Filtered recent transactions - fix channel name matching
  const filteredRecentTransactions = useMemo(() => {
    return recentTransactions.filter(tx => {
      const matchesScheme = filters.scheme === 'All Schemes' || tx.scheme === filters.scheme;
      // Normalize channel names for comparison
      let txChannel = tx.channel;
      if (txChannel === 'MobilePOS') txChannel = 'Mobile POS';
      const matchesChannel = filters.channel === 'All Channels' || txChannel === filters.channel;
      return matchesScheme && matchesChannel;
    });
  }, [recentTransactions, filters.scheme, filters.channel]);
  const handleFilterChange = useCallback((newFilters: any) => {
    setFilters(newFilters);
  }, []);
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  }, []);
  return <DashboardLayout data-id="element-855">
      <div className={`transition-opacity duration-300 ${isRefreshing ? 'opacity-50 pointer-events-none' : 'opacity-100'}`} data-id="element-856">
        {/* Header Section */}
        <div className="mb-6" data-id="element-857">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-858">
            Welcome back, {user?.name || 'Merchant'}
          </h1>
          <p className="text-slate-500 font-medium mt-2 text-base" data-id="element-859">
            Here's what's happening with your business today.
          </p>
        </div>

        {/* Global Filters */}
        <DashboardFilters onFilterChange={handleFilterChange} onRefresh={handleRefresh} data-id="element-860" />

        <div className="flex flex-col xl:flex-row gap-5" data-id="element-861">
          {/* Main Content Area (Left) */}
          <div className="flex-1 flex flex-col gap-5 min-w-0" data-id="element-862">
            {/* Row 1: KPI Cards */}
            <KPICards data={filteredKPIData} data-id="element-863" />

            {/* Row 2: Sales Trend + Channel Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-id="element-864">
              <div className="lg:col-span-2" data-id="element-865">
                <SalesTrend data={filteredTrendData} data-id="element-866" />
              </div>
              <div className="lg:col-span-1" data-id="element-867">
                <SalesByChannel data={filteredChannelData} data-id="element-868" />
              </div>
            </div>

            {/* Row 3: Scheme Distribution + Settlement Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-869">
              <SchemeDistribution data={filteredSchemeData} data-id="element-870" />
              <SettlementSummary data={filteredSettlementData} data-id="element-871" />
            </div>

            {/* Row 4: Branch / Terminal Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-872">
              <BranchPerformance data={filteredBranchData} data-id="element-873" />
              <TerminalPerformance data={filteredTerminalData} data-id="element-874" />
            </div>

            {/* Row 5: Recent Transactions */}
            <RecentTransactions data={filteredRecentTransactions} data-id="element-875" />
          </div>

          {/* Right Panel: Alerts & Notifications */}
          <div className="w-full xl:w-80 flex-shrink-0" data-id="element-876">
            <div className="sticky top-24" data-id="element-877">
              <AlertsPanel data={alerts} data-id="element-878" />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>;
};
export default NewDashboardPage;